import { Injectable, Injector, createNgModule, inject } from '@angular/core';
import { Environment, EnvironmentInitService, SharedService } from '@cfp/core';
import { Observable, firstValueFrom, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { WindowLocationService } from './window-location.service';

@Injectable({
  providedIn: 'root'
})
export class SharedEnvironmentInitService extends EnvironmentInitService {
  private style: HTMLLinkElement;
  private originalBaseHref: string;
  private windowLocationService: WindowLocationService;

  private readonly injector = inject(Injector);
  private readonly sharedService = inject(SharedService);

  constructor() {
    super();

    this.windowLocationService = this.injector.get(WindowLocationService);

    // We need the original base href from the index.html as the local development environment (/csr/app/) is
    // different from the production environment. For production environment it will be fixed to a specific
    // applicaton environment (for example /csr/app/manager/)
    this.originalBaseHref = document.getElementsByTagName('base')[0].attributes['href'].value;
  }

  public init(environmentCode?: string): Promise<void> {
    this.checkHrefAndRedirectWhenNeeded();

    return firstValueFrom(this.setEnvironment(environmentCode || 'shared').pipe(map(() => {})));
  }

  public setEnvironment(environmentCode: string): Observable<boolean> {
    this.setTheme(environmentCode);
    this.setFaviconElements(environmentCode);

    let version: string = '';
    const metaEnvironmentVersion: HTMLMetaElement = document.querySelector('meta[data-environment-version]');
    if (metaEnvironmentVersion) {
      version = `?v=${metaEnvironmentVersion.attributes['data-environment-version'].value}`;
    }

    return from(
      fetch(`environment/${environmentCode}/environment.json${version}`).then(function (response) {
        return response.json();
      })
    ).pipe(
      switchMap((environment: Environment) => {
        this.environment = environment;
        return from(this.preloadModules()).pipe(
          map(() => {
            if (this.environment.addTagManagement) {
              this.addTagManagement(environmentCode);
            }
            return true;
          })
        );
      })
    );
  }

  private getWindowLocationHref(): string {
    const href = this.windowLocationService.getHref();
    return href.endsWith('/') ? href : `${href}/`;
  }

  private checkHrefAndRedirectWhenNeeded(): void {
    const href = this.getWindowLocationHref();
    if (!href.includes(this.originalBaseHref)) {
      // The base href is not found in the url, redirect to the url inclusing the base href
      const urlParts = href.split('://');
      const pathParts = urlParts[1].split('/');
      this.windowLocationService.setHref(`${urlParts[0]}://${pathParts[0]}${this.originalBaseHref}`);
    }
  }

  private preloadModules(): Promise<void> {
    const preloadList = this.environment.preloadModules ?? [];
    if (preloadList.length === 0) {
      this.sharedService.readyLoadingModules.set(true);
      return Promise.resolve();
    }

    this.sharedService.readyLoadingModules.set(false);
    const promises: Promise<void>[] = [];

    preloadList.forEach((module: string) => {
      let promise: Promise<void> | undefined;
      switch (module) {
        case 'klimaatroute.best-value':
          promise = import('../modules/klimaatroute/best-value/app-klimaatroute-best-value-module')
            .then((m) => m.BestValueModuleWithRouting)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'rabobank.app':
          promise = import('../modules/rabobank/app/app-rabobank-module')
            .then((m) => m.RabobankAppModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'knvb':
          promise = import('../modules/knvb/app/app-knvb-module')
            .then((m) => m.KnvbAppModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'natwest':
          promise = import('../modules/natwest/app/app-natwest-module')
            .then((m) => m.NatwestAppModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'dumava':
          promise = import('@cfp/features/dumava/dumava-module')
            .then((m) => m.DumavaModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'compliance':
          promise = import('@cfp/features/compliance')
            .then((m) => m.ComplianceModuleWithRouting)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'confidence-indicator':
          promise = import('@cfp/features/confidence-indicator')
            .then((m) => m.ConfidenceIndicatorModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'sportnlgroen.manager':
          promise = import('../modules/sportnlgroen/app-sportnlgroen-module')
            .then((m) => m.SportNlGroenModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'ing-pl.kalkulator':
          promise = import('../modules/ing-pl/kalkulator/app-ing-pl-kalkulator-module')
            .then((m) => m.IngPlKalkulatorModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'piraeus.lite':
          promise = import('@cfp/features/lite/lite.module')
            .then((m) => m.LiteModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'piraeus.app':
          promise = import('../modules/piraeus/app-piraeus-module')
            .then((m) => m.PiraeusModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        case 'ref.app':
          promise = import('../modules/ref/app-ref-module')
            .then((m) => m.RefAppModule)
            .then((lazyModule) => this.createModule(lazyModule));
          break;

        default:
          console.log(`Missing preload module: ${module}`);
          break;
      }
      if (promise) {
        promises.push(promise);
      }
    });

    return Promise.all(promises).then(() => {
      this.sharedService.readyLoadingModules.set(true);
    });
  }

  private createModule(lazyModule: any): void {
    createNgModule(lazyModule, this.injector);
  }

  private setTheme(environmentCode: string): void {
    if (!this.style) {
      this.style = document.createElement('link');
      this.style.rel = 'stylesheet';
      document.head.appendChild(this.style);
    }

    let href = `${this.originalBaseHref}${environmentCode}.css`;

    const metaStyleVersion: HTMLMetaElement = document.querySelector('meta[data-style-version]');
    if (metaStyleVersion) {
      href += `?v=${metaStyleVersion.attributes['data-style-version'].value}`;
    }
    this.style.href = href;
  }

  private setFaviconElements(environmentCode: string): void {
    this.addLink(`environment/${environmentCode}/assets/favicons/safari-pinned-tab.svg`, 'mask-icon', undefined, {
      color: '#5bbad5'
    });
    this.addLink(`environment/${environmentCode}/assets/favicons/apple-touch-icon.png`, 'apple-touch-icon', undefined, {
      sizes: '180x180'
    });
    this.addLink(`environment/${environmentCode}/assets/favicons/favicon-32x32.png`, 'icon', 'image/png', {
      sizes: '32x32'
    });
    this.addLink(`environment/${environmentCode}/assets/favicons/favicon-16x16.png`, 'icon', 'image/png', {
      sizes: '16x16'
    });
    this.addLink(`environment/${environmentCode}/assets/favicons/safari-pinned-tab.svg`, 'shortcut icon', undefined, {
      color: '#5bbad5'
    });
    this.addLink(`environment/${environmentCode}/assets/favicons/favicon.ico`, 'shortcut icon', undefined, {
      color: '#5bbad5'
    });
    this.addMeta(`environment/${environmentCode}/assets/favicons/mstile-144x144.png`, 'msapplication-TileImage');
    this.addMeta(`environment/${environmentCode}/favicons/browserconfig.xml`, 'msapplication-config');
  }

  private addLink(href: string, rel: string, type?: string, attributes?: { [key: string]: string }): void {
    const link: HTMLLinkElement = document.createElement('link');
    link.href = href;
    link.rel = rel;
    if (type) {
      link.type = type;
    }
    if (attributes) {
      Object.keys(attributes).forEach((key: string) => {
        link.attributes[key] = attributes[key];
      });
    }
    document.head.appendChild(link);
  }

  private addMeta(content: string, name: string): void {
    const meta: HTMLMetaElement = document.createElement('meta');
    meta.content = content;
    meta.name = name;
    document.head.appendChild(meta);
  }

  private addTagManagement(environmentCode: string): void {
    const script: HTMLScriptElement = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `environment/${environmentCode}/assets/js/tag.js`;
    document.head.appendChild(script);
  }
}
